import {
  useLoaderData,
  useRouteError,
  isRouteErrorResponse,
  Link,
  type LoaderFunctionArgs,
} from 'react-router-dom';
import { displayAgentName } from '@/lib/vault/agent-display';
import { fetchMarketplaceAgent } from '@/src/marketplace/client';
import type { MarketplaceAgentManifest } from '@/src/marketplace/types';
import { loadRuntimeConfig } from '@/src/config/runtime';
import { VaultConnectFlow } from '@/src/components/VaultConnectFlow';
import '@/src/styles/connect.css';

export interface ConnectLoaderData {
  agentId: string;
  version: string;
  manifestCid: string;
  agentServicePubkey: string;
  agentName: string;
  settings: NonNullable<MarketplaceAgentManifest['settings']>;
  iconUrl?: string;
  description?: string;
}

export async function connectLoader({ request }: LoaderFunctionArgs): Promise<ConnectLoaderData> {
  const url = new URL(request.url);
  const agentId = url.searchParams.get('agentId');
  if (!agentId) throw new Response('Missing agentId', { status: 400 });

  const config = await loadRuntimeConfig();
  const manifest = await fetchMarketplaceAgent(config.marketplaceBaseUrl, agentId);
  if (!manifest || !manifest.manifestCid || !manifest.agentServicePubkey) {
    throw new Response('Agent not found', { status: 404 });
  }

  const agentName = manifest.card?.name?.trim() || displayAgentName(manifest.agentId);
  return {
    agentId: manifest.agentId,
    version: manifest.version,
    manifestCid: manifest.manifestCid,
    agentServicePubkey: manifest.agentServicePubkey,
    agentName,
    settings: manifest.settings ?? [],
    ...(manifest.card?.iconUrl ? { iconUrl: manifest.card.iconUrl } : {}),
    ...(manifest.card?.description?.trim() ? { description: manifest.card.description.trim() } : {}),
  };
}

export function ConnectPage() {
  const data = useLoaderData() as ConnectLoaderData;
  return <VaultConnectFlow {...data} />;
}

export function ConnectErrorBoundary() {
  const error = useRouteError();
  const isResponse = isRouteErrorResponse(error);
  const title = isResponse && error.status === 404
    ? 'Agent not found'
    : isResponse
      ? `${error.status} ${error.statusText}`
      : 'Could not load connect flow';
  const body = isResponse && error.status === 404
    ? 'The agent you tried to install is not in the Marketplace registry.'
    : error instanceof Error
      ? error.message
      : 'Try opening the Marketplace and selecting an agent again.';
  return (
    <div className="connect-shell">
      <div className="connect-card">
        <h1>{title}</h1>
        <p className="connect-sub">{body}</p>
        <Link className="connect-back" to="/">Back to Vault</Link>
      </div>
    </div>
  );
}
